/**
 * SportDivider — section break between sports in the create-challenge game list
 * (specs/multi-sport-challenges).
 *
 * Leagues already have their own headers; this sits one level above them, so the list reads
 * sport → leagues → games. Rendered for EVERY sport including football, not just the new ones:
 * an unlabelled first block followed by a labelled second block reads as a rendering bug rather
 * than as a deliberate grouping.
 */

import React, { useMemo } from 'react'
import { View, Text } from 'react-native'
import { SportIcon } from '../../../src/sports/components/SportIcon/SportIcon'
import { getSportDefinitionOrDefault } from '../../../src/sports/sports.constants'
import { useTheme } from '../../../src/theme/useTheme'
import { getCreateGamesStepStyles } from './CreateGamesStep.styles'
import { SPORT_DIVIDER_TEST_IDS } from './SportDivider.constants'

export interface SportDividerProps {
  sport?: string | null
  /** Number of games under this sport, shown so the section's weight is obvious. */
  gameCount: number
}

export const SportDivider: React.FC<SportDividerProps> = ({ sport, gameCount }) => {
  const theme = useTheme()
  const styles = useMemo(() => getCreateGamesStepStyles(theme), [theme])
  const definition = getSportDefinitionOrDefault(sport)

  return (
    <View
      style={styles.sportDivider}
      testID={`${SPORT_DIVIDER_TEST_IDS.rootPrefix}${definition.key}`}
      accessibilityRole="header"
      accessibilityLabel={`${definition.label}, ${gameCount} ${gameCount === 1 ? definition.fixtureNoun.one : definition.fixtureNoun.many}`}
    >
      <SportIcon sport={definition.key} size={20} decorative />
      <Text style={styles.sportDividerLabel}>{definition.label.toUpperCase()}</Text>
      <View style={styles.sportDividerRule} />
      <Text style={styles.sportDividerCount}>{gameCount}</Text>
    </View>
  )
}
